import {
	Component,
	ComponentWillLoad,
	Element,
	Event,
	EventEmitter,
	Fragment,
	h,
	Host,
	JSX,
	Listen,
	Prop,
	State,
	Watch
} from '@stencil/core';

import {journeyData, JourneyStoreEvent, NjConnectionData} from "../../global/JourneyStore";
import {formatPrice, registerCssAsset, rootUrl} from '../../helpers/utils';
import {asLocalTime, asOffsetTime, atStartOfDay} from "../../utils/date";
import {DialogHelper} from "../../utils/dialogs";
import {i18n} from '../../utils/I18n';
import {
	doesPassengerRequireWheelchair,
	getOfferPrice,
	getTrainCategoryForName,
	hasWheelchairCompartment
} from "../../utils/offers";
import {FromToData} from '../nj-from-to/nj-from-to';
import {NjLoaderSize} from '../nj-loader/nj-loader-size';
import {AccommodationType} from './AccommodationType';
import {ReservationMode} from '../nj-reservation/ReservationMode';
import {
	buildGalleryItems,
	getCompartmentBookingInformationList,
	getCompartmentInformation,
	getCompartmentTitle,
	IMAGE_DEFAULT_WIDTH,
	IMAGE_WIDTHS,
} from './AccommodationUtils';
import {
	ADDITIONAL_INFORMATION_DIALOGUE_EURO_NIGHT,
	ADDITIONAL_INFORMATION_DIALOGUES,
	isSupportedCompartment,
	isSupportedOffer
} from '../nj-reservation/nj-reservation-utils';
import {
	canUseFemaleCompartment,
	canUseSharedSleeperCompartments,
	getPassangersFilteredFromStore,
	transformNjPassangerDataToTsPrebookObjectsAndRelations
} from '../nj-passangers/passanger-utils';
import {ConnectionWithOffersV6, InformationMessage, Warnings, Warnings_Status} from '../../api/Ticketshop';
import {addOebbDialogModalDefaultStyles} from '@oebb-web-team/oebb-web-library/dist/dialog-modal/OebbDialogModal';
import {getNightTrainCategoryFromString, NightTrainCategory} from './NightTrainCategory';
import {trackMatomoEvent} from "../../utils/matomo";
import {OPEN_PLAN_COACH_PREFIX} from "../nj-offer-list/OfferListConstants";
import {ElementToggler} from "@oebb-web-team/oebb-web-components";
import {TrainInformation, TrainInformationService} from "../../services/train-information.service";
import {RestService} from "../../services/rest.service";
import {scrollToElement} from "../../utils/scroll";

registerCssAsset('icon/icon_check_list_style.svg');
registerCssAsset('img/nightjetSkyBackground.png');

const IMAGE_SIZES = [
	'(max-width: 479px) calc(100vw - 62px)',
	'(max-width: 767px) calc(100vw - 102px)',
	'(max-width: 1240px) calc(33.34vw - 50px)',
	'(max-width: 1280px) calc(33.34vw - 120px)',
	'307px'
].join(', ');

@Component({
	tag: 'nj-connection',
	styleUrls: {
		Outward: 'nj-connection.scss',
		Return: 'nj-connection.scss',
	},
	shadow: false,
})
export class NjConnection implements ComponentWillLoad {
	customId: string = Math.random().toString(36).substr(2, 6);
	compartments: { [key in AccommodationType]?: any[] } = {
		SE: [],
		LE: [],
		BE: [],
	};
	bestPrices = {};
	bestPricePrivate = null;

	@Element() element: HTMLNjConnectionElement;

	@State() isOpen: boolean = false;
	@State() detailCreated: boolean = false;
	@State() offerData: ConnectionWithOffersV6;
	@State() informationMessages: InformationMessage[] = [];
	@State() accommodationType: AccommodationType;
	@State() trainInformation: TrainInformation;

	@Prop() mode: ReservationMode;
	@Prop({ mutable: true }) connection: NjConnectionData;
	@Prop({ mutable: true }) fromToData: FromToData;
	@Prop() parentShadowRoot: ShadowRoot;

	@Prop() linkWarning_temporarilyUnavailable?: string;
	@Prop() linkWarning_noAvailability?: string;
	@Prop() linkWarning_temporarilyInsufficientAvailability?: string;

	//TODO remove feature toggle, after successfull implementation
	@Prop() enableNewInfobox? = false;
	@Prop() enableSearchagent? = false;
	@Prop() simulateWarnings? = false;

	@State() selected?: boolean;
	@State() simulateTemporarilyUnavailable: boolean = false; // Remove after successfull notifier implementation

	@Event() njOfferSearchDone: EventEmitter<{ mode: ReservationMode, connectionWithOffers: ConnectionWithOffersV6 }>;

	private readonly toggler = new ElementToggler('.js-train-details-wrapper', 750);
	private EN_WARNING: InformationMessage = {
		header: i18n.getMessage('reservation.offer-list.euronight.warning'),
		category: 999
	}

	componentWillLoad() {
		addOebbDialogModalDefaultStyles();

		this.loadSelectedConnection();
		this.loadCompartmentInformation();
		this.loadOfferData();


		// Remove after successfull notifier implementation
		const hash = window.location.hash;
		const queryStart = hash.indexOf('?');

		if (queryStart !== -1) {
			const queryString = hash.substring(queryStart + 1);
			const params = new URLSearchParams(queryString);
			const paramValue = params.get('temporarily-unavailable');
			this.simulateTemporarilyUnavailable = paramValue === 'true';
		}
	}

	@Watch('connection')
	onConnectionChanged() {
		this.loadSelectedConnection();
		this.loadCompartmentInformation();
		this.loadOfferData();
	}

	@Listen('njConnectionSelected', {target: 'window'})
	njConnectionSelectedHandler(e: JourneyStoreEvent) {
		if (e.detail.reservationMode === this.mode) {
			// The selected connection for this connection's reservationMode has changed, update the `selected` state
			this.loadSelectedConnection();
			this.loadCompartmentInformation();
		}
	}

	@Listen('njPassengersChanged', {target: 'window'})
	njPassengersChangedHandler() {
		// Passengers were changed from some other component, reload the offers
		this.loadOfferData();
	}

	@Listen('passangersDialogClosed', {target: 'window'})
	passangersDialogClosedHandler(event: CustomEvent) {
		// Passengers were changed in the dialog, reload the offers
		if (event.detail.name === 'passangers') {
			this.loadOfferData();
		}
	}

	loadSelectedConnection() {
		const data = journeyData(this.mode);
		if (data.isChosenNjConnection(this.connection)) {
			this.accommodationType = data.getNjAccommodation();
			this.selected = true;
			this.setOpen(false);
		} else {
			this.accommodationType = null;
			this.selected = false;
		}
	}

	private loadCompartmentInformation() {
		TrainInformationService.instance.getTrainInformation(this.connection.train, this.connection?.departure)
			.then(trainInformation => this.trainInformation = trainInformation)
			.catch(exception => console.error(exception));
	}

	private setOpen(isOpen: boolean) {
		this.isOpen = isOpen;
		if (this.isOpen) {
			this.toggler.show(this.element);
			scrollToElement(this.element);
		}
	}

	async loadOfferData() {
		// Remove all previous offer data
		this.offerData = null;
		this.bestPrices = {};
		this.bestPricePrivate = null;
		this.compartments = {
			SE: [],
			LE: [],
			BE: [],
		};

		// Unselect the connection
		this.accommodationType = null;
		this.selected = false;

		const { objects, relations } = transformNjPassangerDataToTsPrebookObjectsAndRelations();

		if (!objects?.length) {
			this.offerData = {
				offers: [],
				sections: [],
			};
			return;
		}

		let postData = {
			njFrom: +this.connection.from.number,
			njDep: this.connection.from.dep_dt,
			njTo: +this.connection.to.number,
			maxChanges: 0,
			connections: this.connection.trainCount,
			filter: {
				njTrain: this.connection.train,
				njDeparture: this.connection.from.dep_dt,
			},
			objects: objects,
			relations: relations,
			lang: i18n.getLang()
		};

		try {
			const data = await RestService.instance.getOffers(postData);

			if (!data.result?.[0]) {
				this.offerData = {
					offers: [],
					sections: [],
				};
				return;
			}

			const offerData = data.result[0].connections[0];

			for (const offer of offerData.offers) {
				for (const segment of offer.reservation.reservationSegments) {
					for (const compartment of segment.compartments) {
						switch (compartment.accommodationType) {
							case AccommodationType.Se:
								this.compartments[AccommodationType.Se].push({...compartment, offerTitle: offer.name});
								break;
							case AccommodationType.Le:
								this.compartments[AccommodationType.Le].push({...compartment, offerTitle: offer.name});
								break;
							case AccommodationType.Be:
								this.compartments[AccommodationType.Be].push({...compartment, offerTitle: offer.name});
								break;
						}
					}
				}
			}

			this.sortOfferSegmentCompartmentsByPriceAsc(this.compartments[AccommodationType.Se]);
			this.sortOfferSegmentCompartmentsByPriceAsc(this.compartments[AccommodationType.Le]);
			this.sortOfferSegmentCompartmentsByPriceAsc(this.compartments[AccommodationType.Be]);

			this.offerData = offerData;
			this.informationMessages = data.result[0].connections[0].informationMessages;

			this.calculateBestPrices();
		} catch (error) {
			console.error("Error loading offers: ", error);
		} finally {
			this.njOfferSearchDone.emit({
				mode: this.mode,
				connectionWithOffers: this.offerData,
			});
		}
	}

	sortOfferSegmentCompartmentsByPriceAsc(compartments: any) {
		compartments.sort(function (a: any, b: any) {
			let valueA = 0;
			let valueB = 0;

			if (a.privateVariations && a.privateVariations.length > 0) {
				valueA = a.privateVariations[0].allocations[0].objects[0].price;
			} else {
				valueA = a.objects ? a.objects[0].price : 0;
			}

			if (b.privateVariations && b.privateVariations.length > 0) {
				valueB = b.privateVariations[0].allocations[0].objects[0].price;
			} else {
				valueB = b.objects ? b.objects[0].price : 0;
			}

			// DESC
			// if (valueA > valueB) return -1;
			// if (valueA < valueB) return 1;

			// ASC
			if (valueA > valueB) { return 1; }
			if (valueA < valueB) { return -1; }

			return 0;
		});
	}

	calculateBestPrices() {
		if (!this.offerData) return;

		const passangers = getPassangersFilteredFromStore();
		const addCompartmentToBestPrices = (key, keyPrivateOrShared: 'private' | 'shared', price) => {
			if (!this.bestPrices[key]) {
				this.bestPrices[key] = {
					private: null,
					shared: null,
				};
			}

			if (!this.bestPrices[key][keyPrivateOrShared]) {
				this.bestPrices[key][keyPrivateOrShared] = {
					type: key,
					price: price,
				};
			} else if (this.bestPrices[key][keyPrivateOrShared].price > price) {
				this.bestPrices[key][keyPrivateOrShared].price = price;
			}
		};

		for (const offer of this.offerData.offers) {
			if (!isSupportedOffer(offer.name)) { continue; }

			for (const segment of offer.reservation.reservationSegments) {
				for (const compartment of segment.compartments) {
					if (!isSupportedCompartment(compartment)) {
						continue;
					}
					if (compartment.externalIdentifier.includes('female') && !canUseFemaleCompartment(passangers)) {
						continue;
					}
					if (
						/*passangers.length % 2 === 0
						&&*/ (compartment.externalIdentifier.startsWith("double") || compartment.externalIdentifier.startsWith("T3"))
						&& !canUseSharedSleeperCompartments(passangers)
					) {
						continue;
					}
					if (
						/*passangers.length % 3 === 0
						&&*/ compartment.externalIdentifier.startsWith("T3")
						&& !canUseSharedSleeperCompartments(passangers)
					) {
						continue;
					}

					if (compartment.privateVariations) {
						compartment.privateVariations.forEach(privateVariation =>
							addCompartmentToBestPrices(compartment.accommodationType, 'private', getOfferPrice(offer, privateVariation))
						);
					} else {
						addCompartmentToBestPrices(compartment.accommodationType, 'shared', getOfferPrice(offer, compartment));
					}
				}
			}
		}

		for (const key in this.bestPrices) {
			if (!this.bestPrices[key] || !this.bestPrices[key].private) continue;

			if (!this.bestPricePrivate) {
				this.bestPricePrivate = this.bestPrices[key].private;
				continue;
			}

			if (this.bestPrices[key].private && this.bestPrices[key].private.price < this.bestPricePrivate.price) this.bestPricePrivate = this.bestPrices[key].private;
		}
	}

	handleAccordionElementClick(ev: Event) {
		ev.preventDefault();
		if (!this.detailCreated) {
			// Wait for the details to render before opening
			this.detailCreated = true;
			setTimeout(() => this.setOpen(!this.isOpen), 50);
		} else {
			this.setOpen(!this.isOpen);
		}
	}


	handleInfoClick(event: Event, accommodationType: AccommodationType) {
		event.preventDefault();
		event.stopPropagation();

		const trainCategory = getNightTrainCategoryFromString(getTrainCategoryForName(this.connection.train) ?? "EN");
		let dialogSrc = ADDITIONAL_INFORMATION_DIALOGUE_EURO_NIGHT;
		let dialogTitle = i18n.getMessage('reservation.additional-information.euronight');

		if (trainCategory === NightTrainCategory.NIGHTJET) {
			dialogSrc = ADDITIONAL_INFORMATION_DIALOGUES[accommodationType];
			dialogTitle = i18n.getMessage(`ts.accommodation.${accommodationType}`);
		}
		if (dialogSrc) {
			const dialogueObject = DialogHelper.instance.getDialog(dialogSrc, dialogTitle, event.target as HTMLElement);
			dialogueObject.show();
		}
	}

	handleConnectionAccommodationTypeClick(_e: Event, accommodationType: AccommodationType) {
		// _e.preventDefault();

		this.accommodationType = accommodationType;
		this.selected = true;
		this.isOpen = false;
		journeyData(this.mode).setNjOffer(this.connection, this.offerData, accommodationType);
	}

	renderAccommodationIcon(accommodationType: AccommodationType): JSX.Element | null {
		switch (accommodationType) {
			case AccommodationType.Se:
				return <seat-icon class="component-icon"/>;
			case AccommodationType.Le:
				return <couchette-icon class="component-icon"/>;
			case AccommodationType.Be:
				return <sleeper-icon class="component-icon"/>;
			default:
				return null;
		}
	}

	renderBestPricePreview() {
		if (!this.offerData) {
			return (
				<div class="offer-data">
					<nj-loader size={NjLoaderSize.XSMALL}></nj-loader>
				</div>
			);
		}

		//TODO Remove simulation after successfull implementation
		if (this.simulateWarnings && this.simulateTemporarilyUnavailable) {
			this.offerData.warnings = [
				{
					title: 'Simulated Warning',
					sectionIndex: 0,
					status: Warnings_Status.TEMPORARILY_UNAVAILABLE
				}
			]

			return (
				<div class="offer-data">
					<div>
						{ this.offerData.warnings
						? this.renderWarnings(this.offerData.warnings)
						: null}

					</div>
				</div>
			)
		}

		return (
			<div class="offer-data">
				<div>
					{[AccommodationType.Se, AccommodationType.Le, AccommodationType.Be].map(accommodationType => (
						this.bestPrices[accommodationType]?.shared
							? <nj-best-price price={`€ ${formatPrice(this.bestPrices[accommodationType].shared.price)}`} accommodation={accommodationType} selected={(this.accommodationType == accommodationType)}></nj-best-price>
							: null
					))}
					{this.bestPricePrivate ?
							<nj-best-price isPrivate={true} title={this.bestPricePrivate.name} accommodation={this.bestPricePrivate.type} text={this.connection.dani && hasWheelchairCompartment(this.compartments[AccommodationType.Le])
								? i18n.getMessage("connections.starting-prices.multi-function-compartment", null, [formatPrice(this.bestPricePrivate.price)])
								: i18n.getMessage("connections.starting-prices.private-compartment", null, [formatPrice(this.bestPricePrivate.price)])
							}/>
					 : null}
					 {this.offerData.warnings
						? this.renderWarnings(this.offerData.warnings)
						: null}
				</div>
			</div>
		);
	}

	private renderWarnings(warnings: Warnings[]): JSX.Element {
		if (!(warnings?.length > 0)) return;

		return (
			warnings.map(warning =>  {
				const warningLink = `linkWarning_${warning.status}`;

				return (
					<div class="offer-data__warnings" key={warning.status}>
						<span class="offer-data__warning-message">
							{i18n.getMessage(`connections.warnings.${warning.status}`, warning.title)}
							{this.enableNewInfobox &&
								<nj-infobox label={i18n.getMessage(`connections.warnings.${warning.status}`, warning.title)}>
									<p>
										{i18n.getMessage(`connections.warnings.${warning.status}.description`)}
									</p>
								</nj-infobox>
							}
						</span>
						{this.enableSearchagent ?
							(
								warning.status == Warnings_Status.TEMPORARILY_UNAVAILABLE &&
								<nj-notifier
									departureDate={this.connection.departure}
									departureStation={this.connection.from.name}
									departureNumber={this.fromToData?.from?.context?.number}
									arrivalDate={this.connection.arrival}
									arrivalStation={this.connection.to.name}
									arrivalNumber={this.fromToData?.to?.context?.number}
									train={this.connection.train}
								/>
							) :
							this[warningLink] && (
								<a class="offer-data__warning-link" href={this[warningLink]} target="_blank">
									{i18n.getMessage('reservation.additional-information')}
								</a>
							)
						}
					</div>
				)
			})
		);
	}

	getCompartmentTitle(accommodationType: AccommodationType) {
		return getCompartmentTitle(accommodationType);
	}

	renderCompartmentIcon(accommodationType: AccommodationType) {
		switch (accommodationType) {
			case AccommodationType.Se:
				return <seat-icon />;
			case AccommodationType.Le:
				return <couchette-icon />;
			case AccommodationType.Be:
				return <sleeper-icon />;
		}
	}

	private renderCompartmentImages(accommodationType: AccommodationType) {
		const galleryItems = buildGalleryItems(
			getCompartmentInformation(this.trainInformation, accommodationType)?.images,
			IMAGE_DEFAULT_WIDTH,
			IMAGE_WIDTHS
		);

		return galleryItems?.length ? (
			<div class="accommodation-overview__image">
				<oebbx-gallery-slider download-enabled="false"
									  embedded="true"
									  enlargable="true"
									  sizes={IMAGE_SIZES}
									  items={galleryItems}
									  onClick={this.stopPropagation}
									  data-visible="true"
				></oebbx-gallery-slider>
			</div>
		) : null;
	}

	private readonly stopPropagation = (event: Event) => {
		event.stopPropagation();
	}

	private renderCompartmentText(accommodationType: AccommodationType) {
		const compartmentInformation = getCompartmentInformation(this.trainInformation, accommodationType);
		return (
			<Fragment>
				{this.renderCompartmentProperties(compartmentInformation.highlightedProperties, "accommodation__text-highlighted-properties")}
				{this.renderCompartmentProperties(compartmentInformation.regularProperties)}
			</Fragment>
		)
	}

	private renderCompartmentProperties(properties: string[], cssClass?: string) {
		return properties?.length ? (
			<ul class={cssClass}>
				{properties.map(property => (
					<li innerHTML={property}></li>
				))}
			</ul>
		) : null;
	}

	renderBookingInformation(accommodationType: AccommodationType) {
		if (!doesPassengerRequireWheelchair()) {
			const compartmentBookingInformationList = getCompartmentBookingInformationList(
				accommodationType,
				this.areOnlyOpenPlanSeatsAvailable(accommodationType)
						|| (this.connection.dani && accommodationType !== AccommodationType.Be)
			);
			if (compartmentBookingInformationList.length === 3) {
				return (
					<p>
						{compartmentBookingInformationList[0]}
						<button class="compartment-booking-information-link"
							aria-label={i18n.getMessage("reservation.additional-information")}
							onClick={event => this.handleCompartmentBookingInformationClick(event)}
						>
							{compartmentBookingInformationList[1]}
						</button>
						{compartmentBookingInformationList[2]}
					</p>
				);
			} else if (compartmentBookingInformationList.length === 2) {
				return (
					<p>
						{compartmentBookingInformationList[0]}
						{compartmentBookingInformationList[1]}
					</p>
				);
			} else {
				return (<p>{i18n.getMessage("reservation.offer-list.seating-car.booking-information.invalid-message-count")}</p>);
			}
		} else {
			return null;
		}
	}

	handleCompartmentBookingInformationClick(event: Event) {
		event.preventDefault();
		event.stopPropagation();

		trackMatomoEvent({ "event": "nj.searcconnection.privateCompartment.information" });
		const dialogSrc = `${rootUrl()}/${i18n.getLang()}/komfortkategorien/ganzes-abteil-buchen-buchungstool`;
		const dialogueObject = DialogHelper.instance.getDialog(dialogSrc, i18n.getMessage("reservation.additional-information.compartment-booking"), event.target as HTMLElement);
		dialogueObject.show();
	}

	areOnlyOpenPlanSeatsAvailable(accommodationType: AccommodationType): boolean {
		if (accommodationType === AccommodationType.Se) {
			for (const externalIdentifier of this.compartments?.SE.map((compartment) => compartment.externalIdentifier)) {
				if (externalIdentifier.indexOf(OPEN_PLAN_COACH_PREFIX) === (-1)) {
					return false;
				}
			}
			return true;
		}
		return false;
	}

	renderOfferData() {
		if (!this.offerData) {
			return (
				<div class="offer-data">
					<nj-loader size={NjLoaderSize.XSMALL}></nj-loader>
				</div>
			);
		}

		const category = getTrainCategoryForName(this.connection.train) ?? "EN";

		return (
			<div class="segment">
				{Object.keys(this.compartments).map((key: any) => (
					key in this.bestPrices && this.compartments[key].length > 0 ? (
						<section class="accommodation accommodation-has-offers" onClick={(e: Event) => this.handleConnectionAccommodationTypeClick(e, key)}>
							{this.renderCompartmentImages(key)}
							<h3 class={`accommodation__title accommodation__title--${category}`}>
								{this.renderCompartmentIcon(key)} {this.getCompartmentTitle(key)}
							</h3>
							<div class="accommodation__detail">
								<div class="accommodation__text">{this.renderCompartmentText(key)}</div>
								<div class="accommodation__booking_information">{this.renderBookingInformation(key)}</div>

								<div class="accommodation__price-action">
									<div class="accommodation__price-grid">
										{this.bestPrices[key].shared ? (
											<div class="accommodation__price">
												<span class="accommodation__price-from">{i18n.getMessage("connection.individual-seat-from")}</span>
												<span class="accommodation__price-value-container">
													<span>{i18n.getMessage("connection.price.starting-from") + " "}</span>
													<span class="accommodation__price-value"><span>&euro;&nbsp;</span><span>{formatPrice(this.bestPrices[key].shared.price)}</span></span>
												</span>
											</div>
										) : null}
										{this.bestPrices[key].private ? (
											<div class="accommodation__price">
												<span class="accommodation__price-from">
													{this.connection.dani && hasWheelchairCompartment(this.compartments[key])
														? i18n.getMessage("connection.multifunction-compartment-from")
														: i18n.getMessage("connection.entire-compartment-from")
													}
												</span>
												<span class="accommodation__price-value-container">
													<span>{i18n.getMessage("connection.price.starting-from") + " "}</span>
													<span class="accommodation__price-value"><span>&euro;&nbsp;</span><span>{formatPrice(this.bestPrices[key].private.price)}</span></span>
												</span>
											</div>
										) : null}
										<button onClick={(event) => this.handleInfoClick(event, key)} class="button--secondary full-height-button btn-no-border-radius">
											{i18n.getMessage("reservation.additional-information")}
										</button>
										<button class="button--primary full-height-button full-width-button btn-no-border-radius" onClick={(e: Event) => this.handleConnectionAccommodationTypeClick(e, key)}>
											{this.accommodationType == key ? [<check-icon />, i18n.getMessage("connection.selected")] : i18n.getMessage("connection.select")}
										</button>
									</div>
								</div>
							</div>
						</section>
					) : (
						<section class="accommodation">
							<h3 class={`accommodation__title accommodation__title--${category}`}>
								{this.renderCompartmentIcon(key)} {this.getCompartmentTitle(key)}
							</h3>

							<div class="accommodation__detail">
								{key === 'SE' && this.connection.seatAsIC && this.connection.seatAsICText ? (
									<nj-ticket-link connection={this.connection}></nj-ticket-link>
								) : (
									<p>{i18n.getMessage('connection.no-offers')}</p>
								)}
							</div>
						</section>
					)
				))}
			</div>
		);
	}

	renderDetails() {
		if (this.offerData?.warnings?.length > 0) return;

		if (this.detailCreated && this.isOpen) {
			return (
				<div class="js-train-details-wrapper train-details-wrapper">
					<div class="train-details">
						<nj-information-messages messages={this.informationMessages} />

						<div class="train-details-offer">{this.renderOfferData()}</div>

						<p class="price-disclaimer">{i18n.getMessage("connection.price-disclaimer")}</p>
					</div>
				</div>
			);
		}
	}

	private isRequestDate(trainDateTime: number): boolean {
		const formDate = this.mode == ReservationMode.Return ? this.fromToData?.dateReturn?.context : this.fromToData?.dateOutward?.context;
		if (!formDate) {
			return false;
		}

		const trainDate = atStartOfDay(asOffsetTime(trainDateTime));
		const requestDate = atStartOfDay(asLocalTime(formDate));
		return +trainDate === +requestDate;
	}

	render() {
		const category = getTrainCategoryForName(this.connection.train) ?? "EN";

		return <Host class={{'connection-open': this.isOpen}}>
			<div
				id={this.customId}
				class={{
					'connection-header': true,
					'connection-header--selected': this.selected,
				}}
			>
				<div class="inner connection-header__overview">
					{this.isRequestDate(this.connection.from.dep_dt) && <span class="train__request-date"><calendar-check-icon width={14} height={14} />{i18n.getMessage("connection.selected_date")}</span>}
					<nj-connection-overview
						showCalendar={true}
						selected={this.selected}
						category={category}
						departureDate={this.connection.departure}
						arrivalDate={this.connection.arrival}
						departureStation={this.connection.from.name}
						arrivalStation={this.connection.to.name}
						trains={[this.connection.train]}
						duration={this.connection.duration_fmt}
						informationMessages={category == 'EN' ? [this.EN_WARNING] : null}
					>
					</nj-connection-overview>
					<nj-information-messages messages={this.informationMessages} onlySummary={true} />
					{this.renderBestPricePreview()}
				</div>
				{ !(this.offerData?.warnings?.length > 0) ?
					<button id={this.customId} class="connection__toggle"
						onClick={ev => this.handleAccordionElementClick(ev)}
						aria-pressed={`${this.isOpen}`}
						disabled={this.offerData?.warnings?.length > 0}
					>
						<span class="connection__toggle-text">{this.isOpen ? i18n.getMessage('offers.hide') : i18n.getMessage('offers.show')}</span>
						{this.isOpen ? <arrow-up-icon /> : <arrow-down-icon />}
					</button>
					: <div id={this.customId} class="connection__toggle"></div> }
				{ !(this.offerData?.warnings?.length > 0) ?
					<div class="connection-header__selection">
						{this.selected
							? [<span class="screen-reader-text">{i18n.getMessage("connection.selected")}</span>]
							: [<span class="screen-reader-text">{i18n.getMessage("connection.not-selected")}</span>]}
					</div>
					: null }
			</div>
			{this.renderDetails()}
		</Host>
	}
}
